import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState, useCallback } from 'react';
import {
  FormContext,
  FormContextProvider,
  FormSchemaFields,
} from '@sw-sw/lib-form';
import FormModal from '../../Shared/form/modal/FormModal';
import { UIControlType } from '@sw-sw/lib-form-control-types';
import { AppDivisionContext } from '../../../contexts/AppDivisionContext';
import tenantApi from '../../../utils/api/tenant';
import AppContext from '../../../contexts/AppContext';
import useResource from '../../../hooks/resource';
import inspectionTemplateApi from '../../../utils/api/inspectionTemplate';

const getSchema = (formData, role) => {
  const tenants = formData.tenants;
  const divisionIds = formData.divisionIds;

  const sch = {
    tenantId: {
      label: 'Tenant',
      controlType: UIControlType.select,
      options: tenants,
      labelKey: 'name',
      valueKey: 'id',
      validation: {
        required: true,
      },
      parse: (val) => Number.parseInt(val, 10),
    },
    divisionIds: {
      controlType: UIControlType.customTagSelect,
      labelKey: 'name',
      valueKey: 'id',
      isMulti: true,
      openOnFocus: true,
      showClearAll: true,
      label: 'Divisions',
      options: divisionIds || [],
      noOptionMessage: 'No divisions found',
      placeholder: 'Search for divisions',
      validation: {
        required: true,
      },
    },
  };

  if (role === 'Admin') {
    delete sch.tenantId;
  }

  return sch;
};

const handleSubmit = (formData, callback) => {
  return inspectionTemplateApi
    .copy(formData.tenantId, formData.divisionIds, formData.selectedItems)
    .then(callback);
};

function InspectionTemplateCopyUI({
  onCancel,
  onSubmit,
  title,
  cancelText,
  showSubmit,
  initialValue,
  formData,
  setFormData,
  role,
}) {
  const formContext = useContext(FormContext);
  const setRegulations = useCallback(
    (tenantId) => {
      tenantApi.divisions
        .index(tenantId)
        .then((data) => {
          setFormData({
            ...formData,
            divisionIds: data.length ? data : [],
          });

          if (
            formContext.value.divisionIds &&
            formContext.value.divisionIds.length
          ) {
            formContext.set(
              'divisionIds',
              formContext.value.divisionIds.filter((c) =>
                data.map((_) => _.id).includes(c.id),
              ),
            );
          } else {
            formContext.set('divisionIds', []);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    [formContext.value.tenantId],
  );

  useEffect(() => {
    if (formContext.value.tenantId) {
      setRegulations(formContext.value.tenantId);
    }
  }, [formContext.value.tenantId]);

  return (
    <FormModal
      modalProps={{
        title,
        isSmall: false,
      }}
      onCancel={onCancel}
      // eslint-disable-next-line no-shadow
      onSubmit={(formData) => {
        return handleSubmit(formContext.value, onSubmit);
      }}
      showSubmit={showSubmit}
      cancelText={cancelText}
    >
      <div className='legend-item-form'>
        <FormSchemaFields
          onChange={formContext.set}
          formData={formContext.value}
          schema={getSchema(formData, role)}
        />
      </div>
    </FormModal>
  );
}

export default function InspectionTemplateCopy({ selectedItems, ...props }) {
  const appStore = useContext(AppContext);

  const user = appStore.get('user');

  const [initialValue] = useState({
    tenantId: user.tenantId || '',
    divisionIds: [],
    selectedItems: selectedItems,
  });

  const appDivisionContext = useContext(AppDivisionContext);

  const formDataQuery =
    appDivisionContext.appDivisionId &&
    useResource({
      resource: 'tenants',
      disablePagination: true,
      divisionId: appDivisionContext.appDivisionId,
    });

  const [loaded, setLoaded] = useState(false);

  // form options for selection of clients/roles/projects
  const [formData, setFormData] = useState({
    tenants: formDataQuery.data,
  });

  useEffect(() => {
    if (!loaded && !formDataQuery.loading) {
      setFormData({
        tenants: formDataQuery.data,
      });
      setLoaded(true);
    }
  }, [formDataQuery]);

  // only render when loaded and initial value is set
  if (!loaded || !initialValue) {
    return null;
  }

  return (
    <FormContextProvider initialValue={initialValue} key={'LiCopyFormUI'}>
      <InspectionTemplateCopyUI
        initialValue={initialValue}
        formData={formData}
        setFormData={setFormData}
        title={'Copy Inspection Templates'}
        {...props}
      />
    </FormContextProvider>
  );
}

InspectionTemplateCopy.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  title: PropTypes.string,
  cancelText: PropTypes.string,
  showSubmit: PropTypes.bool,
};

InspectionTemplateCopy.defaultProps = {
  readOnly: false,
  showSubmit: true,
  enableDelete: false,
};
